import { NACE_COMPETENCIES } from 'constants/nace-competencies';
import { Box, Typography } from '@mui/material';
import { Button, Modal, ModalProps, TextInput, TextArea } from 'components/ui';
import React, { ReactElement, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useSchoolsApi } from 'services/api';
import Image from 'next/image';

interface LearningOutcomeData {
  id: string;
  title: string;
  description: string;
  naceCompetencies: string[];
}

export interface ManageLearningOutcomesModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {
  schoolId: string;
  learningOutcome?: LearningOutcomeData;
}

export const ManageLearningOutcomesModal = ({
  open,
  schoolId,
  onClose,
  learningOutcome,
}: ManageLearningOutcomesModalProps): ReactElement => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCompetencies, setSelectedCompetencies] = useState<string[]>([]);

  const { enqueueSnackbar } = useSnackbar();
  const schoolsApi = useSchoolsApi();

  const isEditMode = !!learningOutcome;

  useEffect(() => {
    if (learningOutcome) {
      setTitle(learningOutcome.title);
      setDescription(learningOutcome.description);
      setSelectedCompetencies(learningOutcome.naceCompetencies || []);
    } else {
      setTitle('');
      setDescription('');
      setSelectedCompetencies([]);
    }
  }, [learningOutcome]);

  const toggleCompetency = (competency: string) => {
    setSelectedCompetencies(prev =>
      prev.includes(competency)
        ? prev.filter(c => c !== competency)
        : [...prev, competency],
    );
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      enqueueSnackbar('Please enter a title', { variant: 'error' });
      return;
    }

    if (!description.trim()) {
      enqueueSnackbar('Please enter a description', { variant: 'error' });
      return;
    }

    if (!schoolId) {
      enqueueSnackbar('School ID is required', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        const result = await schoolsApi.updateLearningOutcome(learningOutcome.id, {
          title: title.trim(),
          description: description.trim(),
          naceCompetencies: selectedCompetencies,
        });

        if (result.success) {
          enqueueSnackbar('Learning outcome updated successfully', { variant: 'success' });
          onClose?.();
        } else {
          enqueueSnackbar(result.error?.message || 'Failed to update learning outcome', { variant: 'error' });
        }
      } else {
        const result = await schoolsApi.createLearningOutcome(schoolId, {
          title: title.trim(),
          description: description.trim(),
          naceCompetencies: selectedCompetencies,
        });

        if (result.success) {
          enqueueSnackbar('Learning outcome created successfully', { variant: 'success' });
          setTitle('');
          setDescription('');
          setSelectedCompetencies([]);
          onClose?.();
        } else {
          enqueueSnackbar(result.error?.message || 'Failed to create learning outcome', { variant: 'error' });
        }
      }
    } catch (error) {
      enqueueSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} learning outcome`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={isEditMode ? 'Edit Learning Outcome' : 'Add Learning Outcome'}
      open={open}
      onClose={onClose}
    >
      <Box sx={{ width: { xs: '100%', md: 600 }, maxWidth: { xs: '100%', md: '80vw' } }}>
        <Box width="100%">
          <TextInput
            label="Learning Outcome"
            width="100%"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Enter learning outcome title"
          />
        </Box>

        <Box mt={20} width="100%">
          <TextArea
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Enter learning outcome description"
            rows={4}
          />
        </Box>

        <Box mt={20} width="100%">
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              color: '#383838',
              mb: 4,
            }}
          >
            NACE Competencies
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={8}>
            {NACE_COMPETENCIES.map(competency => {
              const isSelected = selectedCompetencies.includes(competency);
              return (
                <Box
                  key={competency}
                  onClick={() => toggleCompetency(competency)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 40,
                    px: 8,
                    py: '3px',
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: isSelected ? '#383838' : '#c8cfe0',
                    backgroundColor: '#f4f6fb',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    gap: 4,
                    userSelect: 'none',
                    '&:hover': {
                      borderColor: '#383838',
                    },
                  }}
                >
                  {isSelected && (
                    <Image src="/icons/check-competency.svg" alt="" width={16} height={16} />
                  )}
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: isSelected ? 500 : 400,
                      color: '#383838',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {competency}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box mt={30} display="flex" flexDirection="column" alignItems="center" gap={24}>
          <Button
            variant="default"
            hoverEffect="none"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ width: 320 }}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
          <Typography
            onClick={() => !isSubmitting && onClose?.()}
            sx={{
              fontSize: '0.875rem',
              color: '#383838',
              textDecoration: 'underline',
              cursor: isSubmitting ? 'default' : 'pointer',
              textAlign: 'center',
            }}
          >
            Cancel
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};
